import { useState, useMemo, useCallback, useEffect } from "react";
import { useModal } from "components/Modal/context/ModalContext";
import { Button, ButtonType } from "components/Button/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy, faCheck, faCircleCheck, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import "./ShareModal.scss";
import { DataFetcher } from "../DataFetcher";
import useFetch from "hooks/useFetch";
import { getFetchOptions } from "../../utils/fetch.js";

const ShareModal = ({ shareLink, contentName }) => {
	const { closeModal } = useModal();
	const [companyUsers, setCompanyUsers] = useState([]);
	const [selectedEmailUsers, setSelectedEmailUsers] = useState([]);
	const [copied, setCopied] = useState(false);
	const fetchDataUsers = { url: "/api/v3/sharecontent/users" };
	const { execute } = useFetch();
	const [feedback, setFeedback] = useState();
	const [sendFailed, setSendFailed] = useState(false);
	const [showConfirm, setShowConfirm] = useState(false);
	const [isSending, setIsSending] = useState(false);
	const [secondsLeft, setSecondsLeft] = useState(3);

	const handleFetchData = useCallback((users) => {
		if (users) {
			setCompanyUsers(users);
			setSelectedEmailUsers((prev) =>
				users.filter(u => prev.some(p => p.Email === u.Email))
			);
		}
	}, []);

	const fullLink = useMemo(() => {
		if (!shareLink) {
			return "";
		}
		if (/^https?:\/\//i.test(shareLink)) {
			return shareLink;
		}

		const origin =
			typeof window !== "undefined" && window.location
				? window.location.origin
				: "";
		const path = shareLink.startsWith("/") ? shareLink : `/${shareLink}`;
		return `${origin}${path}`;
	}, [shareLink]);

	const selectedCount = selectedEmailUsers.length;
	const allCount = companyUsers.length;
	const isAllSelected = allCount > 0 && selectedCount === allCount;

	const toggleSelect = (person) => {
		setSelectedEmailUsers((prev) => {
			const exists = prev.some((p) => p.Email === person.Email);
			return exists ? prev.filter((p) => p.Email !== person.Email) : [...prev, person];
		});
	};

	const isSelected = (email) => selectedEmailUsers.some((p) => p.Email === email);

	const handleSelectAllChange = (e) => {
		const checked = e.target.checked;
		if (checked) {
			setSelectedEmailUsers(companyUsers);
		} else {
			setSelectedEmailUsers([]);
		}
	};

	const copyToClipboard = async () => {
		try {
			if (navigator.clipboard && window.isSecureContext) {
				await navigator.clipboard.writeText(fullLink);
			} else {
				const ta = document.createElement("textarea");
				ta.value = fullLink;
				ta.setAttribute("readonly", "");
				ta.style.position = "absolute";
				ta.style.left = "-9999px";
				document.body.appendChild(ta);
				ta.select();
				document.execCommand("copy");
				document.body.removeChild(ta);
			}
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		} catch {
			setCopied(false);
		}
	};

	// open confirmation
	const handleSendSelected = () => {
		setShowConfirm(true);
	};

	// send
	const sendLinkToSelectedUsers = (link, courseName, users) => {
		setIsSending(true);

		const payload = {
			Url: link,
			ContentName: courseName,
			Recipients: users.reduce((acc, item) => {
				acc[item.Email] = item.Name;
				return acc;
			}, {})
		};

		const options = getFetchOptions({
			method: "POST",
			body: payload
		});

		execute("/api/v3/sharecontent/share", options, (fetchedData, error) => {
			if (error) {
				setFeedback(error);
				setSendFailed(true);
			} else {
				setFeedback("Your share link has been sent.");
				setSendFailed(false);
			}

			setIsSending(false);
			setShowConfirm(false);
		});
	};

	const handleConfirmYes = useCallback(() => {
		if (isSending) {
			return;
		}

		sendLinkToSelectedUsers(fullLink, contentName, selectedEmailUsers);
	}, [isSending, fullLink, contentName, selectedEmailUsers]);

	useEffect(() => {
		if (feedback && !sendFailed) {
			setSecondsLeft(3);
			const interval = setInterval(() => {
				setSecondsLeft((s) => {
					if (s <= 1) {
						clearInterval(interval);
						closeModal();
						return 0;
					}
					return s - 1;
				});
			}, 1000);
			return () => clearInterval(interval);
		}
	}, [feedback, sendFailed, closeModal]);

	return (
		<div className="share-modal">
			<DataFetcher fetchData={fetchDataUsers} setData={handleFetchData}>
				<h2>Share {contentName}</h2>

				<h3>Share via Link</h3>
				<p>
					Share via a link to send to others. They will need to have a ucSKILLS account to view the content.
				</p>
				<div className="share-modal-link">
					<p className="share-modal-link-text">{fullLink}</p>
					<div
						className={`share-modal-link-copy ${copied ? "copied" : ""}`}
						role="button"
						tabIndex={0}
						onClick={copyToClipboard}
						onKeyDown={(e) => {
							if (e.key === "Enter" || e.key === " ") copyToClipboard();
						}}
						aria-label="Copy link to clipboard"
					>
						<span className="share-modal-link-copy-text">
							{copied ? "Copied" : "Copy Link"}
						</span>
						<FontAwesomeIcon
							icon={copied ? faCheck : faCopy}
							className="share-modal-link-copy-icon"
						/>
					</div>
				</div>

				{(!showConfirm && !feedback) && (
					<>
						<h3>Share via Email</h3>
						<p>Send a link to the content directly to people's inbox.</p>

						<div className="share-modal-select-all">
							<input
								id="select-all-checkbox"
								type="checkbox"
								checked={isAllSelected}
								onChange={handleSelectAllChange}
								aria-checked={isAllSelected}
							/>
							<label htmlFor="select-all-checkbox" className="select-all-checkbox">
								Select all {allCount > 0 ? `(${selectedCount}/${allCount})` : ""}
							</label>
						</div>

						<div className="share-modal-user-list">
							{companyUsers.map((person) => (
								<div
									key={person.Email}
									onClick={() => toggleSelect(person)}
									onKeyDown={(e) => {
										if (e.key === "Enter" || e.key === " ") toggleSelect(person);
									}}
									role="button"
									tabIndex={0}
									aria-pressed={isSelected(person.Email)}
									className={`share-modal-user-list-user ${isSelected(person.Email) ? "selected" : ""}`}
								>
									<p className="share-modal-user-list-user-name">
										{person.Name} <span>({person.Email})</span>
									</p>
								</div>
							))}
						</div>

						<div className="modal-buttons">
							<Button
								type={ButtonType.Secondary}
								text="Cancel"
								onClick={() => closeModal()}
							/>
							<Button
								type={ButtonType.Primary}
								text={`Send to ${selectedCount} ${selectedCount === 1 ? "person" : "people"}`}
								onClick={handleSendSelected}
								disabled={selectedCount === 0}
							/>
						</div>
					</>
				)}
			</DataFetcher>

			{showConfirm && (
				<div
					className="share-modal-confirm-backdrop"
					role="presentation"
					onClick={() => !isSending && setShowConfirm(false)}
				>
					<div
						className="share-modal-confirm"
						role="dialog"
						aria-modal="true"
						aria-labelledby="confirm-title"
						aria-describedby="confirm-desc"
						onClick={(e) => e.stopPropagation()}
					>
						<h3 id="confirm-title">Confirm send</h3>
						<p id="confirm-desc">
							Clicking Yes will share the content with {selectedCount} {selectedCount === 1 ? "person" : "people"}. Are you sure you want to send it?
						</p>
						<div className="modal-buttons">
							<Button
								type={ButtonType.Secondary}
								text="No"
								onClick={() => setShowConfirm(false)}
								disabled={isSending}
							/>
							<Button
								type={ButtonType.Primary}
								text={isSending ? "Sending..." : "Yes"}
								onClick={handleConfirmYes}
								disabled={isSending}
							/>
						</div>
					</div>
				</div>
			)}

			{feedback && (
				<div className={`modal-feedback ${sendFailed ? "failed" : ""}`}>
					<div className="modal-feedback-text">
					<FontAwesomeIcon
						icon={sendFailed ? faTriangleExclamation : faCircleCheck}
						className="modal-feedback-icon"
					/>
						<p>{feedback}</p>
					</div>
					{!sendFailed && (
						<p>This modal will close in {secondsLeft} second{secondsLeft === 1 ? "" : "s"}.</p>
					)}
				</div>
			)}
		</div>
	);
};

export default ShareModal;
